import { useState, useEffect, useCallback, useRef } from "react";
import { readListCache, writeListCache, userListScope } from "@/utils/listCache";
import { useNavigate, useLocation } from "react-router-dom";
import {
  IonCard, IonCardHeader, IonCardTitle, IonList, IonItem, IonItemGroup, IonItemDivider,
  IonLabel, IonAvatar, IonButton, IonIcon, IonSpinner, IonInput,
} from "@ionic/react";
import { linkOutline, sendOutline, trashOutline, eyeOutline, chatbubbleOutline, addOutline, createOutline, copyOutline, cloudUploadOutline, documentAttachOutline } from "ionicons/icons";
import RowActions from "@/components/RowActions";
import SendSignDocSheet from "@/components/SendSignDocSheet";
import EditSheet from "@/components/EditSheet";
import { FLOAT } from "@/components/SettingsRows";
import CommentsThreadModal from "@/components/CommentsThreadModal";
import DocumentPreviewModal from "@/components/DocumentPreviewModal";
import AppLayout from "@/components/AppLayout";
import AppSeo from "@/components/AppSeo";
import { uiAvatar } from "@/components/SidebarProfileCard";
import { useSavedAssets } from "@/components/SavedAssets";
import { isUserLoggedIn, userHeaders } from "@/utils/userAuth";
import { useAppLive } from "@/utils/appLive";
import { confirmAlert } from "@/utils/confirmAlert";
import { toast } from "@/utils/toast";
import { toastText } from "@/utils/toastTemplates";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const BADGE = {
  pending: ["admin-badge-amber", "Pending"],
  signed: ["admin-badge-green", "Signed"],
  declined: ["admin-badge-red", "Declined"],
  canceled: ["admin-badge-slate", "Canceled"],
  expired: ["admin-badge-slate", "Expired"],
};
// a final signature waiting for the sender's approval
const awaiting = (r) => r.status === "signed" && r.approval === "pending";

// /app/signatures: the sender's tracker for documents sent out for
// signature — status at a glance, copy the link, nudge a slow signer,
// cancel one, and jump to the signed copy. Pro/Enterprise, like the rest
// of the business features.
// The page's segment row in the top bar (the Tax Layouts / Preferences
// pattern): the requests, and the trusted signers. ?segment=trusted opens
// the second.
const SEGMENTS = [
  { value: "requests", label: "Signature Requests", to: "/app/signatures" },
  { value: "documents", label: "Documents", to: "/app/signatures?segment=documents" },
  { value: "trusted", label: "Trusted Signers", to: "/app/signatures?segment=trusted" },
];
const SIGN_DOC_MAX_MB = 8;

export default function AppSignatures() {
  const navigate = useNavigate();
  const location = useLocation();
  const wantedSeg = new URLSearchParams(location.search).get("segment");
  const segment = ["trusted", "documents"].includes(wantedSeg) ? wantedSeg : "requests";
  // the phone row folds its action icons into one menu button (RowActions),
  // so the row stays one line, the height of the Trusted Signers and Documents rows
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const { hasAccess, loading } = useSavedAssets();
  const [rows, setRows] = useState(() => readListCache("signatures", userListScope())?.rows ?? null); // the last rows paint at once
  const [busyId, setBusyId] = useState("");
  const [threadFor, setThreadFor] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null); // the archived signed copy, in the notification-style pager
  const loadPreviewFile = async (doc) => {
    const res = await fetch(`${BACKEND_URL}/api/my-documents/${doc.id}/download`, { headers: userHeaders() });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${res.status}`); }
    return res.blob();
  };
  // Which comments were already viewed, per request: the badge shows only
  // what arrived since. The signing page keeps its total count.
  const SEEN_KEY = "sigCommentsSeen";
  const readSeen = () => { try { return JSON.parse(localStorage.getItem(SEEN_KEY) || "{}"); } catch { return {}; } };
  const [seen, setSeen] = useState(readSeen);
  const markSeen = (id, count) => {
    const next = { ...readSeen(), [id]: count };
    try { localStorage.setItem(SEEN_KEY, JSON.stringify(next)); } catch { /* quota */ }
    setSeen(next);
  };
  const unseenCount = (r) => Math.max(0, (r.comments || []).length - (seen[r.id] || 0));
  const openThread = (r) => { markSeen(r.id, (r.comments || []).length); setThreadFor(r.id); };
  // the whole row opens the signing page (the sender's login skips the gate)
  const openSignPage = (r) => {
    const token = String(r.link || "").split("/sign/")[1];
    if (token) navigate(`/sign/${encodeURIComponent(token)}`);
  };
  const [commentText, setCommentText] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);

  // Trusted signers: their signatures approve themselves
  // Uploaded documents for signature (the Templates page's list)
  const [docs, setDocs] = useState(() => readListCache("signatureDocs", userListScope())?.docs ?? null);
  const [docBusy, setDocBusy] = useState("");
  const [sendDoc, setSendDoc] = useState(null);  // a document (with its fields) being sent
  const fileInput = useRef(null);
  const loadDocs = useCallback(async () => {
    if (!isUserLoggedIn()) { setDocs([]); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/sign-documents`, { headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.success !== false) { setDocs(d.documents || []); writeListCache("signatureDocs", { docs: d.documents || [] }, userListScope()); }
      else setDocs((p) => p || []);
    } catch { setDocs((p) => p || []); }
  }, []);
  useEffect(() => { loadDocs(); }, [loadDocs]);
  useAppLive(loadDocs);
  const uploadDoc = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) { toast.error("Upload a PDF"); return; }
    if (file.size > SIGN_DOC_MAX_MB * 1024 * 1024) { toast.error(`PDFs up to ${SIGN_DOC_MAX_MB} MB can be uploaded`); return; }
    setDocBusy("upload");
    try {
      const fileData = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(",")[1]);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const res = await fetch(`${BACKEND_URL}/api/user/sign-documents`, {
        method: "POST", headers: userHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ fileName: file.name, fileData }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not upload");
      navigate(`/app/signatures/documents/${d.document.id}`);
    } catch (err) { toast.error(err.message); } finally { setDocBusy(""); }
  };
  const docAction = async (doc, path, method = "POST") => {
    setDocBusy(doc.id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/sign-documents/${encodeURIComponent(doc.id)}${path}`, { method, headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Action failed");
      loadDocs();
      return d;
    } catch (err) { toast.error(err.message); return null; } finally { setDocBusy(""); }
  };
  const openSendDoc = async (doc) => {
    setDocBusy(doc.id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/sign-documents/${encodeURIComponent(doc.id)}`, { headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.document) throw new Error(d.detail || "Could not open the document");
      if (!(d.document.fields || []).some((f) => f.role !== "sender")) { toast.error("Place fields for a signer first"); navigate(`/app/signatures/documents/${doc.id}`); return; }
      setSendDoc(d.document);
    } catch (err) { toast.error(err.message); } finally { setDocBusy(""); }
  };
  const deleteDoc = async (doc) => {
    if (!(await confirmAlert({ header: "Delete this document?", message: `${doc.name} is removed. Requests already sent are not affected.`, confirmText: "Delete" }))) return;
    if (await docAction(doc, "", "DELETE")) toast.success("Deleted");
  };

  const [trusted, setTrusted] = useState([]);
  const [trustDraft, setTrustDraft] = useState("");
  const [trustSheet, setTrustSheet] = useState(false); // the Add modal
  const [trustBusy, setTrustBusy] = useState(false);
  const loadTrusted = useCallback(async () => {
    if (!isUserLoggedIn()) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/trusted-signers`, { headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.success !== false) setTrusted(d.signers || []);
    } catch { /* offline */ }
  }, []);
  useEffect(() => { loadTrusted(); }, [loadTrusted]);
  useAppLive(loadTrusted); // switching businesses swaps the list
  const addTrusted = async () => {
    const email = trustDraft.trim();
    if (!email) { toast.error("Enter an email address"); return; }
    setTrustBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/trusted-signers`, {
        method: "POST", headers: userHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ email }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not add");
      setTrusted(d.signers || []);
      setTrustDraft("");
      setTrustSheet(false);
      toast.success(`${email} added`);
    } catch (err) { toast.error(err.message); } finally { setTrustBusy(false); }
  };
  const removeTrusted = async (email) => {
    if (!(await confirmAlert({ header: "Remove this trusted signer?", message: `Signatures from ${email} will wait for your approval again.`, confirmText: "Remove" }))) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/trusted-signers?email=${encodeURIComponent(email)}`, { method: "DELETE", headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not remove");
      setTrusted(d.signers || []);
    } catch (err) { toast.error(err.message); }
  };

  const load = useCallback(async () => {
    if (!isUserLoggedIn()) { setRows([]); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/signature-requests`, { headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.success !== false) { setRows(d.requests || []); writeListCache("signatures", { rows: d.requests || [] }, userListScope()); }
      else setRows([]);
    } catch { setRows((p) => p || []); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useAppLive(load);

  useEffect(() => {
    if (!loading && !hasAccess) navigate("/app/billing", { replace: true });
  }, [loading, hasAccess, navigate]);

  const copyLink = async (r) => {
    try {
      await navigator.clipboard.writeText(r.link);
      toast.success(toastText("sign_link_copied", {}, "Link copied. Share it any way you like"));
    } catch { toast.error("Could not copy the link"); }
  };
  const remind = async (r) => {
    setBusyId(r.id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/signature-requests/${r.id}/remind`, { method: "POST", headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not remind");
      toast.success(toastText("sign_reminder_sent", { email: r.recipientEmail }, "Reminder sent to {email}"));
      load();
    } catch (err) { toast.error(err.message); } finally { setBusyId(""); }
  };
  const cancel = async (r) => {
    if (!(await confirmAlert({ header: "Cancel this request?", message: `${r.recipientEmail}'s link stops working.`, confirmText: "Cancel it" }))) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/signature-requests/${r.id}`, { method: "DELETE", headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not cancel");
      toast.success(toastText("sign_request_canceled", {}, "Request canceled"));
      load();
    } catch (err) { toast.error(err.message); }
  };

  const threadComments = (rows || []).find((x) => x.id === threadFor)?.comments || [];
  const addComment = async (images = []) => {
    const text = commentText.trim();
    if (!text && images.length === 0) { toast.error("Write a comment first"); return false; }
    setCommentBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/signature-requests/${threadFor}/comments`, {
        method: "POST", headers: userHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ text, images }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not add the comment");
      setRows((p) => (p || []).map((x) => (x.id === threadFor ? { ...x, comments: d.comments } : x)));
      markSeen(threadFor, (d.comments || []).length);
      setCommentText("");
      return true;
    } catch (err) { toast.error(err.message); return false; } finally { setCommentBusy(false); }
  };

  const titleCase = (v) => String(v || "").split(" ").map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(" ");
  const line = (r) => {
    const sent = r.createdAt ? new Date(r.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
    const extra = r.status === "declined" && r.declineReason ? ` · "${r.declineReason}"`
      : r.status === "pending" && r.forwardedTo ? "" : r.forwardedTo ? ` · sent on to ${r.forwardedTo}` : "";
    const docName = r.documentType === "custom-pdf" ? (r.signDocName || "Document") : titleCase(r.documentLabel);
    const order = r.documentType === "custom-pdf" && (r.signerCount || 0) > 1 ? ` · signer ${(r.signerIndex || 0) + 1} of ${r.signerCount}` : "";
    return `${docName}${order} · sent ${sent}${r.remindCount ? ` · ${r.remindCount} reminder${r.remindCount === 1 ? "" : "s"} sent` : ""}${extra}`;
  };

  const awaitingRows = (rows || []).filter(awaiting);
  const requestRows = (rows || []).filter((r) => !["signed", "declined", "expired"].includes(r.status));
  const signedRows = (rows || []).filter((r) => r.status === "signed" && !awaiting(r));
  const declinedRows = (rows || []).filter((r) => r.status === "declined" || r.status === "expired");

  // a request's actions: icon buttons on a wide screen, the menu's rows on the phone
  const rowActions = (r) => [
    r.status === "signed" && r.docId && { key: "preview", icon: eyeOutline, label: "Preview signed copy", run: () => setPreviewDoc({ id: r.docId, fileName: `${titleCase(r.documentLabel)} (signed).pdf` }) },
    { key: "comments", icon: chatbubbleOutline, label: "Comments", count: unseenCount(r), run: () => openThread(r) },
    { key: "link", icon: linkOutline, label: "Copy signing link", run: () => copyLink(r) },
    r.status === "pending" && { key: "remind", icon: sendOutline, label: "Send reminder", busy: busyId === r.id, run: () => remind(r) },
    (awaiting(r) || r.status === "pending") && { key: "cancel", icon: trashOutline, label: "Cancel request", color: "danger", run: () => cancel(r) },
  ].filter(Boolean);

  const renderRow = (r, idx, list) => {
    const [cls, text] = awaiting(r) ? ["admin-badge-amber", "Awaiting approval"] : (BADGE[r.status] || ["admin-badge-slate", r.status]);
    return (
      <IonItem key={r.id} button detail={false} onClick={() => openSignPage(r)}
        lines={idx === list.length - 1 ? "none" : undefined} className="mod-row sig-row">
        <div className="sig-main">
          <IonAvatar className="sig-avatar"><img alt="" src={uiAvatar(r.recipientName || r.recipientEmail)} /></IonAvatar>
          <div className="sig-text">
            <div className="sig-name">
              <span className="sig-who">{r.recipientName || r.recipientEmail}</span>
              <span className={`admin-badge ${cls}`}>{text}</span>
            </div>
            <p className="sig-sub">{line(r)}</p>
          </div>
          <RowActions actions={rowActions(r)} isMobile={isMobile} />
        </div>
      </IonItem>
    );
  };

  return (
    <AppLayout
      pageTabs={{
        value: segment,
        items: SEGMENTS,
        dropdownOnMobile: true, // a phone folds the tabs into the top bar's dropdown
        // replace, not push: segment hops stay one history entry
        onChange: (v) => { const sg = SEGMENTS.find((x) => x.value === v); if (sg) navigate(sg.to, { replace: true }); },
      }}
    >
      <AppSeo title="Signatures" description="Documents you've sent out for signature." path="/app/signatures" />
      {rows === null || loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><IonSpinner name="crescent" /></div>
      ) : (
        <div className="admin-profile admin-moderators app-signatures" style={{ maxWidth: 1200, margin: "0 auto", paddingBottom: 40 }}>
          {segment === "requests" && (
          <IonCard className="mods-card">
            <IonCardHeader>
              <IonCardTitle className="type-title"><span>Signature Requests</span></IonCardTitle>
            </IonCardHeader>
            <IonList>
              {awaitingRows.length > 0 && (
                <IonItemGroup>
                  <IonItemDivider className="border-top">
                    <IonLabel>Awaiting your approval</IonLabel>
                    <span slot="end" className="mods-count">{awaitingRows.length}</span>
                  </IonItemDivider>
                  {awaitingRows.map((r, idx) => renderRow(r, idx, awaitingRows))}
                </IonItemGroup>
              )}
              <IonItemGroup>
                <IonItemDivider className="border-top">
                  <IonLabel>Sent for signature</IonLabel>
                  <span slot="end" className="mods-count">{requestRows.length}</span>
                </IonItemDivider>
                {requestRows.length === 0 ? (
                  <IonItem lines="none"><IonLabel color="medium">{rows.length === 0 ? "Nothing sent yet. Send a document for signature from its form's signature area." : "Nothing waiting. Everything sent has been signed."}</IonLabel></IonItem>
                ) : requestRows.map((r, idx) => renderRow(r, idx, requestRows))}
              </IonItemGroup>
              {signedRows.length > 0 && (
                <IonItemGroup>
                  <IonItemDivider className="border-top">
                    <IonLabel>Signed</IonLabel>
                    <span slot="end" className="mods-count">{signedRows.length}</span>
                  </IonItemDivider>
                  {signedRows.map((r, idx) => renderRow(r, idx, signedRows))}
                </IonItemGroup>
              )}
              {declinedRows.length > 0 && (
                <IonItemGroup>
                  <IonItemDivider className="border-top">
                    <IonLabel>{declinedRows.some((r) => r.status === "expired") ? "Declined or expired" : "Declined"}</IonLabel>
                    <span slot="end" className="mods-count">{declinedRows.length}</span>
                  </IonItemDivider>
                  {declinedRows.map((r, idx) => renderRow(r, idx, declinedRows))}
                </IonItemGroup>
              )}
            </IonList>
          </IonCard>
          )}

          {/* ── Trusted Signers: the Employees card, with the recurring
                 card's top-right button; their signatures approve themselves ── */}
          {/* ── Documents: uploaded PDFs with placed signature fields, the
                 Templates page's list; each can be sent again and again ── */}
          {segment === "documents" && (
            <IonCard className="mods-card sig-docs-card">
              <IonCardHeader>
                <IonCardTitle className="type-title">
                  <span>Documents</span>
                  <IonButton size="small" fill="outline" disabled={docBusy === "upload"} onClick={() => fileInput.current && fileInput.current.click()}
                    title="Upload a PDF to send for signature"
                    style={{ "--color": "var(--ion-text-color, #0d1b2a)", "--border-color": "var(--ion-text-color, #0d1b2a)" }}>
                    {docBusy === "upload" ? <IonSpinner name="crescent" style={{ width: 16, height: 16 }} /> : <><IonIcon slot="start" icon={cloudUploadOutline} /> Upload PDF</>}
                  </IonButton>
                </IonCardTitle>
              </IonCardHeader>
              <input ref={fileInput} type="file" accept="application/pdf,.pdf" style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; uploadDoc(f); }} />
              <IonList>
                <IonItemGroup>
                  <IonItemDivider className="border-top">
                    <IonLabel>Your documents · reusable</IonLabel>
                    <span slot="end" className="mods-count">{(docs || []).length}</span>
                  </IonItemDivider>
                  {docs === null ? (
                    <IonItem lines="none"><IonSpinner name="crescent" /></IonItem>
                  ) : docs.length === 0 ? (
                    <IonItem lines="none"><IonLabel color="medium" className="ion-text-wrap">No documents yet. Upload a PDF, place where each person signs, initials, dates or fills in, then send it to them in order. You can send it again any time.</IonLabel></IonItem>
                  ) : docs.map((doc, idx) => (
                    <IonItem key={doc.id} lines={idx === docs.length - 1 ? "none" : undefined} className="mod-row tpl-row" button detail={false} onClick={() => navigate(`/app/signatures/documents/${doc.id}`)}>
                      <IonIcon slot="start" icon={documentAttachOutline} style={{ fontSize: 26, color: "var(--ion-color-primary)" }} />
                      <IonLabel>
                        {doc.name}
                        <p>{doc.fieldCount} field{doc.fieldCount === 1 ? "" : "s"} · {(doc.roles || []).length} signer{(doc.roles || []).length === 1 ? "" : "s"}{doc.sentCount ? ` · sent ${doc.sentCount} time${doc.sentCount === 1 ? "" : "s"}` : ""}</p>
                      </IonLabel>
                      {/* the row's links: icons wide, the signature requests' three-dot menu on the phone */}
                      <RowActions slot="end" isMobile={isMobile} actions={[
                        { key: "send", icon: sendOutline, label: "Send for signature", busy: docBusy === doc.id, run: () => openSendDoc(doc) },
                        { key: "edit", icon: createOutline, label: "Edit fields", run: () => navigate(`/app/signatures/documents/${doc.id}`) },
                        { key: "duplicate", icon: copyOutline, label: "Duplicate", run: () => docAction(doc, "/duplicate").then((d) => d && toast.success("Duplicated")) },
                        { key: "delete", icon: trashOutline, label: "Delete", color: "danger", run: () => deleteDoc(doc) },
                      ]} />
                    </IonItem>
                  ))}
                </IonItemGroup>
              </IonList>
            </IonCard>
          )}

          {segment === "trusted" && (
            <IonCard className="mods-card sig-trusted-card">
              <IonCardHeader>
                <IonCardTitle className="type-title">
                  <span>Trusted Signers</span>
                  <IonButton size="small" fill="outline" onClick={() => { setTrustDraft(""); setTrustSheet(true); }}
                    title="Add a trusted signer"
                    style={{ "--color": "var(--ion-text-color, #0d1b2a)", "--border-color": "var(--ion-text-color, #0d1b2a)" }}>
                    <IonIcon slot="start" icon={addOutline} /> Add
                  </IonButton>
                </IonCardTitle>
              </IonCardHeader>
              <IonList>
                <IonItemGroup>
                  <IonItemDivider className="border-top">
                    <IonLabel>Signatures from these addresses approve themselves</IonLabel>
                    <span slot="end" className="mods-count">{trusted.length}</span>
                  </IonItemDivider>
                  {trusted.length === 0 ? (
                    <IonItem lines="none"><IonLabel color="medium">None yet. Everyone's signatures wait for your approval.</IonLabel></IonItem>
                  ) : trusted.map((email, idx) => (
                    <IonItem key={email} lines={idx === trusted.length - 1 ? "none" : undefined} className="mod-row">
                      <IonAvatar slot="start"><img alt="" src={uiAvatar(email)} /></IonAvatar>
                      <IonLabel>
                        {email}
                        <p>Auto-approved</p>
                      </IonLabel>
                      <IonButton slot="end" fill="clear" color="danger" size="small" className="app-row-icon-btn" onClick={() => removeTrusted(email)} aria-label={`Remove ${email}`} title="Remove">
                        <IonIcon slot="icon-only" icon={trashOutline} />
                      </IonButton>
                    </IonItem>
                  ))}
                </IonItemGroup>
              </IonList>
            </IonCard>
          )}
          <DocumentPreviewModal doc={previewDoc} loadFile={loadPreviewFile} onClose={() => setPreviewDoc(null)} />
          <CommentsThreadModal isOpen={!!threadFor} onClose={() => { if (threadFor) markSeen(threadFor, threadComments.length); setThreadFor(null); }}
            comments={threadComments} value={commentText} onValue={setCommentText}
            busy={commentBusy} onSubmit={addComment}
            readOnly={(() => {
              // a signature waiting for approval keeps its thread open
              const r = (rows || []).find((x) => x.id === threadFor) || {};
              return (r.status === "signed" && !awaiting(r)) || ["declined", "expired", "canceled"].includes(r.status);
            })()} />
        </div>
      )}
      {sendDoc && (
        <SendSignDocSheet doc={sendDoc} onClose={() => setSendDoc(null)}
          onSent={() => { setSendDoc(null); load(); loadDocs(); navigate("/app/signatures", { replace: true }); }} />
      )}
      {trustSheet && (
        <EditSheet title="Add trusted signer" onClose={() => setTrustSheet(false)} onSave={addTrusted} saving={trustBusy} saveLabel="Add">
          <IonInput {...FLOAT} label="Email address" type="email" inputmode="email" value={trustDraft}
            onIonInput={(e) => setTrustDraft(e.detail.value ?? "")} onKeyDown={(e) => { if (e.key === "Enter" && !trustBusy) addTrusted(); }} />
          <p style={{ margin: "10px 2px 0", fontSize: "0.82rem", color: "var(--ion-color-medium)" }}>
            Signatures from this address approve themselves. Everyone else's wait for your approval before the final signed copy is made.
          </p>
        </EditSheet>
      )}
    </AppLayout>
  );
}
